/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { SetStateAction, useCallback, useMemo, useState } from 'react';
import { Grid, Tab, Tabs } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import {
  CreatorRole,
  GetChangeDto,
  UpdateChangeDto,
  UpdateChangeDtoStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
/**
 * The internal dependencies
 */
import { useTranslation } from 'react-i18next';
import CalculationResultPage from './CalculationResultPage';
import CalculationHoursPage from './CalculationHoursPage';
import { useChangeStore } from '../../store/changeStore';
import { useChangeApiFactory, useProcessApiFactory } from '../../ApiClient';
import LoadSpinner from '../../components/LoadSpinner/LoadSpinner';
import useLoading from '../../utils/useLoading';
import { NotificationTypes } from '../../types/notification';
import NotificationMessages from '../../constants/notification';
import { useStorex } from '../../store/UIStore';
import CalculationAttachmentsTab from './CalculationAttachmentsTab';
import {
  isCalculationPerson,
  isChangeManager,
} from '../../utils/permissionHelper';
import ChangeStatusDialogWrapper from '../../components/Dialog/ChangeStatusDialogWrapper';
import { DialogType } from '../../types/changes';
import ButtonsGroup from '../../components/ButtonsGroup/ButtonsGroup';
import CalculationServiceMaterialTab from './CalculationServiceMaterialTab';
import NumbersPanel from '../../components/Panel/NumbersPanel';

const CalculationPage = observer(() => {
  const [tabIndex, setTabIndex] = useState(0);
  const {
    change,
    reducedWorkPackages,
    isDataLoading,
    setChange,
    setUpdatedChange,
    workPackages,
    reducedWorkMaterials,
    reducedServices,
    getTotalWorkMaterialHoursOfUsedProcesses,
  } = useChangeStore();
  const { setNotification } = useStorex();
  const { changeId } = useParams();
  const { isLoading } = useLoading();
  const NotificationMessage = NotificationMessages();

  const [modalType, setModalType] = useState<DialogType | null>(null);
  // Add state for sum of materials and services

  const { t } = useTranslation();

  const showButtonGroup =
    change?.status === UpdateChangeDtoStatusEnum.EvalAccepted ||
    change?.status === UpdateChangeDtoStatusEnum.EvalCalcRejected;

  const isManager = isChangeManager(change);
  const isCalcResponsible = isCalculationPerson(change);

  const changeApi = useChangeApiFactory();
  const processApi = useProcessApiFactory();

  const showSuccessNotification = useCallback(
    () =>
      setNotification({
        type: NotificationTypes.success,
        message: NotificationMessage.changeStatusUpdate,
      }),
    [NotificationMessage.changeStatusUpdate, setNotification],
  );

  const showErrorNotification = useCallback(
    () =>
      setNotification({
        type: NotificationTypes.error,
        message: NotificationMessage.error,
      }),
    [NotificationMessage.error, setNotification],
  );

  const debounceChangeRequest = useDebouncedCallback(
    async (data: { [key: string]: number }) => {
      try {
        if (changeId && change) {
          const { data: response } = await changeApi.changesControllerUpdate({
            id: Number(changeId),
            updateChangeDto: {
              ...data,
              status: change.status as UpdateChangeDtoStatusEnum,
              finalQuotationValue: 1,
            },
          });
          if (response) {
            setChange({
              ...change,
              ...response,
              commonProject: change.commonProject,
            });

            const { data: secondReponse } =
              await changeApi.changesControllerUpdate({
                id: Number(changeId),
                updateChangeDto: {
                  finalQuotationValue:
                    Number(response?.finalQuotationValue) -
                    (response?.projectDiscount ?? 0) +
                    (response?.materialValue ?? 0),
                  status: change.status as UpdateChangeDtoStatusEnum,
                },
              });

            setChange({
              ...change,
              ...secondReponse,
              commonProject: change.commonProject,
            });

            showSuccessNotification();
          }
        }
      } catch (e) {
        showErrorNotification();
        console.error(e);
      }
    },
    300,
  );

  const debouncedRequest = useDebouncedCallback(
    async (processId, data: { [key: string]: number }) => {
      try {
        const { data: updatedProcess } =
          await processApi.processesControllerUpdate({
            id: processId,
            updateProcessDto: {
              ...data,
            },
          });

        if (updatedProcess && change) {
          const updatedProcesses = change?.processes.map(process => {
            if (process.id === updatedProcess.id) {
              const updatedCurrnetProcess = {
                ...process,
                ...updatedProcess,
              };
              return updatedCurrnetProcess;
            }
            return process;
          });

          const newChangeRequest: GetChangeDto | UpdateChangeDto = {
            ...change,
            processes: updatedProcesses,
          };

          setUpdatedChange(newChangeRequest);
        }

        if (updatedProcess) {
          showSuccessNotification();
        }
      } catch (e) {
        showErrorNotification();
        console.error(e);
      }
    },
    1000,
  );

  const affectedProcesses = useMemo(
    () => change?.processes.filter(process => !process.affected) || [],
    [change?.processes],
  );

  const handleSwitchTab = useCallback(
    (event: any, newValue: SetStateAction<number>) => {
      setTabIndex(newValue);
    },
    [],
  );

  const handleChangeOrProcess = useCallback(
    (key: string, value: number, processId?: number) => {
      if (processId) debouncedRequest(processId, { [key]: value });
      else debounceChangeRequest({ [key]: value });
    },
    [debounceChangeRequest, debouncedRequest],
  );

  const calculatedMaterialCosts = getTotalWorkMaterialHoursOfUsedProcesses(
    CreatorRole.CalculationResponsible,
  );

  return (
    <Grid
      container
      direction="row"
      css={css`
        width: 100%;
        justify-content: space-between;
        margin-top: 13px;
        width: 100%;
      `}
    >
      {isDataLoading && <LoadSpinner />}

      {!isDataLoading && (
        <Grid size={12} container>
          <Grid size={9}>
            <Tabs
              css={css`
                .MuiTabs-flexContainer {
                  gap: 17px;
                }
              `}
              variant="scrollable"
              value={tabIndex}
              aria-label="Calculation Tabs"
              onChange={handleSwitchTab}
            >
              <Tab
                label={t('calc.evaluatedHours')}
                css={css`
                  font-size: 18px;
                  font-weight: 500;
                  padding: 14px 25px;
                  color: #00223b;
                `}
              />
              <Tab
                label={t('calc.materialsServicesHeading')}
                css={css`
                  font-size: 18px;
                  font-weight: 500;
                  padding: 14px 25px;
                  color: #00223b;
                `}
              />
              <Tab
                label={t('documents.docs')}
                css={css`
                  font-size: 18px;
                  font-weight: 500;
                  padding: 14px 25px;
                  color: #00223b;
                `}
              />
            </Tabs>
            <CalculationHoursPage
              isTabDataVisible={tabIndex === 0}
              processes={affectedProcesses}
              workPackages={reducedWorkPackages}
              workMaterials={reducedWorkMaterials}
              workServices={reducedServices}
              isLoading={isLoading}
              handleChangeProcess={handleChangeOrProcess}
              change={change || null}
              editable={isManager || isCalcResponsible}
            />
            <CalculationServiceMaterialTab
              isTabDataVisible={tabIndex === 1}
              processes={affectedProcesses}
              workMaterials={reducedWorkMaterials}
              workPackages={reducedWorkPackages}
              services={reducedServices}
              isLoading={isLoading}
              handleChangeProcess={handleChangeOrProcess}
              change={change || null}
              editable={isManager || isCalcResponsible}
            />

            <CalculationAttachmentsTab
              change={change || null}
              isTabDataVisible={tabIndex === 2}
              editable={isManager || isCalcResponsible}
              showError={() => showErrorNotification}
            />
          </Grid>
          {!isDataLoading && (
            <Grid size={3}>
              {change && showButtonGroup && (
                <ButtonsGroup
                  isChangeExist={!!change}
                  changeStatus={change?.status || ''}
                  workPackages={workPackages}
                  change={change}
                  onClickBtn={e =>
                    setModalType(e.currentTarget.dataset.type as DialogType)
                  }
                />
              )}
              {change && <NumbersPanel change={change} />}
              <CalculationResultPage
                materialValue={calculatedMaterialCosts}
                handleChangeProcess={handleChangeOrProcess}
                editable={isManager || isCalcResponsible}
                change={change || null}
              />
            </Grid>
          )}
        </Grid>
      )}

      <ChangeStatusDialogWrapper
        modalType={modalType}
        setModalType={m => setModalType(m)}
      />
    </Grid>
  );
});

export default CalculationPage;
